import { GameData } from '@/common/types/scopeAndSequence';
import { ControllerType } from '@/common/types/student';
import { stopAudio } from '@/common/utils/audio';
import { getWordById } from '@/common/utils/scopeAndSequence';
import { EventBus } from '@/game/GameComponents/PhaserGame/EventBus';
import {
  GameInstance,
  getGeneralGameState,
  getSession,
  getStudent,
} from '@/game/GameComponents/PhaserGame/GameInstance';
import { StudentGameInputHandler } from '@/game/GameComponents/PhaserGame/Input/StudentGameInputHandler';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { ResponsivenessManager } from '@/game/GameComponents/PhaserGame/Managers/ResponsivenessManager';
import { getSceneManager } from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import { HeartWordsController } from '@/game/GameComponents/PhaserGame/MinigameControllers/HeartWordsController';
import { MinigameController } from '@/game/GameComponents/PhaserGame/MinigameControllers/MinigameController';
import { MorphMasterController } from '@/game/GameComponents/PhaserGame/MinigameControllers/MorphMasterController';
import { MysticMatchController } from '@/game/GameComponents/PhaserGame/MinigameControllers/MysticMatchController';
import { PhonemeForgeController } from '@/game/GameComponents/PhaserGame/MinigameControllers/PhonemeForgeController';
import { SentenceBuilderController } from '@/game/GameComponents/PhaserGame/MinigameControllers/SentenceBuilderController';
import { WordRiftsController } from '@/game/GameComponents/PhaserGame/MinigameControllers/WordRiftsController';
import { WordSumsController } from '@/game/GameComponents/PhaserGame/MinigameControllers/WordSumsController';
import { WordWeaverController } from '@/game/GameComponents/PhaserGame/MinigameControllers/WordWeaverController';
import { SettingsPanelController } from '@/game/GameComponents/PhaserGame/Scenes/Settings/SettingsPanelController';
import StudentGame from '@/game/GameComponents/PhaserGame/StudentGames/StudentGame';
import { MinigameCardData } from '@/game/GameComponents/PhaserGame/Types/IMinigameCardData';
import { DEFAULT_SCALE_DOWN_CONFIG } from '@/game/GameComponents/PhaserGame/Types/ManagerTypes/ResponsivenessManagerTypes';
import GAME_EVENTS from '@/game/GameComponents/PhaserGame/Utils/gameEventsDef';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import { HorizontalLayoutGroup } from '@/game/GameComponents/PhaserGame/Utils/PhaserLayout/HorizontalLayoutGroup';
import {
  biomeKeys,
  didAttemptEarnSpeedBonus,
  DoReinforcementFeedback,
  getAnchorPlugin,
  getArticulatoryGestureTexture,
  getBiomeFromStudentLevel,
  isPhaserDevMode,
  shouldShowGraphemeExampleWordButton,
  shouldShowWordHelperButtons,
  waitForMilliseconds,
  waitForSeconds,
  waitUntil,
} from '@/game/GameComponents/PhaserGame/Utils/PhaserUtils';
import DefaultButton from '@/game/prefabs/GeneralUI/Buttons/DefaultButton';
import HUDButton from '@/game/prefabs/GeneralUI/Buttons/HUDButton';
import CheckboxSmall from '@/game/prefabs/GeneralUI/CheckboxSmall';
import DefinitionExampleView from '@/game/prefabs/GeneralUI/DefinitionExampleView';
import LoadingSpinner from '@/game/prefabs/GeneralUI/LoadingSpinner';
import ProgressBar from '@/game/prefabs/GeneralUI/ProgressBar';
import UIBlocker from '@/game/prefabs/GeneralUI/UIBlocker';
import MinigameInstructions from '@/game/prefabs/Minigame/MinigameInstructions';
import MinigameScoreScreen from '@/game/prefabs/Minigame/MinigameScoreScreen';
import NoTypingNotification from '@/game/prefabs/Minigame/NoTypingNotification';
import SpeedBonusFeedbackContainer from '@/game/prefabs/Minigame/SpeedBonusFeedbackContainer';
import ArticulatoryGesture from '@/game/prefabs/StudentGame/ArticulatoryGesture';
import CardDropSite from '@/game/prefabs/StudentGame/CardDropSite';
import PlayerTypingInput from '@/game/prefabs/StudentGame/PlayerTypingInput';
import SequencedCardDropSite from '@/game/prefabs/StudentGame/SequencedCardDropSite';
import SlottedCardDropSite from '@/game/prefabs/StudentGame/SlottedCardDropSite';
import StudentGameCard from '@/game/prefabs/StudentGame/StudentGameCard';
import LevelMap from '@/game/scenes/LevelMap';
import _ from 'lodash';

/* START OF COMPILED CODE */

export default class Minigame extends StudentGame {

	constructor() {
		super("Minigame");

		/* START-USER-CTR-CODE */
    // Write your code here.
    /* END-USER-CTR-CODE */
	}

	editorCreate(): void {

		// FinalBacking
		const finalBacking = this.add.image(0, 0, "1x1sprite");
		finalBacking.scaleX = 1024;
		finalBacking.scaleY = 768;
		finalBacking.setOrigin(0, 0);
		finalBacking.tintTopLeft = 985612;
		finalBacking.tintTopRight = 985612;
		finalBacking.tintBottomLeft = 985612;
		finalBacking.tintBottomRight = 985612;

		// uIBlocker
		const uIBlocker = new UIBlocker(this, 0, 0);
		this.add.existing(uIBlocker);

		// gameframe
		const gameframe = this.add.container(0, 0);
		gameframe.scaleX = 4;
		gameframe.scaleY = 4;

		// BG2
		const bG2 = this.add.image(0, 0, "spring-board-BG1");
		bG2.setOrigin(0, 0);
		gameframe.add(bG2);

		// BG
		const bG = this.add.image(0, 0, "spring-board-BG2");
		bG.setOrigin(0, 0);
		gameframe.add(bG);

		// board-BR
		const board_BR = this.add.image(1600, 1024, "spring-board-BR");
		board_BR.scaleX = 2;
		board_BR.scaleY = 2;
		board_BR.setOrigin(1, 1);

		// board-TL
		const board_TL = this.add.image(0, 0, "spring-board-TL");
		board_TL.scaleX = 2;
		board_TL.scaleY = 2;
		board_TL.setOrigin(0, 0);

		// board-TR
		const board_TR = this.add.image(1600, 0, "spring-board-TR");
		board_TR.scaleX = 2;
		board_TR.scaleY = 2;
		board_TR.setOrigin(1, 0);

		// board-BL
		const board_BL = this.add.image(0, 1024, "spring-board-BL");
		board_BL.scaleX = 2;
		board_BL.scaleY = 2;
		board_BL.setOrigin(0, 1);

		// mainContainer
		const mainContainer = this.add.container(0, 0);

		// playerInputView
		const playerInputView = this.add.container(792, 1008);
		mainContainer.add(playerInputView);

		// handBG
		const handBG = this.add.nineslice(0, 0, "Hud3", undefined, 100, 42, 16, 16, 16, 16);
		handBG.scaleX = 3;
		handBG.scaleY = 3;
		handBG.setOrigin(0.5, 1);
		playerInputView.add(handBG);

		// submitButton
		const submitButton = new DefaultButton(this, 0, -93);
		submitButton.scaleX = 0.75;
		submitButton.scaleY = 0.75;
		submitButton.visible = true;
		playerInputView.add(submitButton);

		// handView
		const handView = this.add.container(0, -308);
		playerInputView.add(handView);

		// handDropSite
		const handDropSite = new SequencedCardDropSite(this, 0, 68);
		handView.add(handDropSite);

		// handDropZoneOverride
		const handDropZoneOverride = this.add.image(8, 136, "1x1sprite");
		handDropZoneOverride.scaleX = 1600;
		handDropZoneOverride.scaleY = 400;
		handDropZoneOverride.alpha = 0;
		handDropZoneOverride.alphaTopLeft = 0;
		handDropZoneOverride.alphaTopRight = 0;
		handDropZoneOverride.alphaBottomLeft = 0;
		handDropZoneOverride.alphaBottomRight = 0;
		handView.add(handDropZoneOverride);

		// handDropSite_1
		const handDropSite_1 = new SequencedCardDropSite(this, 0, 56);
		handView.add(handDropSite_1);

		// gameButtonsContainer
		const gameButtonsContainer = this.add.container(0, -39);
		gameButtonsContainer.scaleX = 1.5;
		gameButtonsContainer.scaleY = 1.5;
		playerInputView.add(gameButtonsContainer);

		// repeatInstructionButton
		const repeatInstructionButton = new HUDButton(this, 20, 0, "ui-repeat-button");
		repeatInstructionButton.scaleX = 2;
		repeatInstructionButton.scaleY = 2;
		gameButtonsContainer.add(repeatInstructionButton);

		// autoWinButton
		const autoWinButton = new HUDButton(this, 56, 0, "ui-submit-button");
		autoWinButton.scaleX = 2;
		autoWinButton.scaleY = 2;
		gameButtonsContainer.add(autoWinButton);

		// playDefinitionButton
		const playDefinitionButton = new HUDButton(this, -20, 0, "ui-definition-button");
		playDefinitionButton.scaleX = 2;
		playDefinitionButton.scaleY = 2;
		gameButtonsContainer.add(playDefinitionButton);

		// playWordInSentenceButton
		const playWordInSentenceButton = new HUDButton(this, -56, 0, "ui-speak-button");
		playWordInSentenceButton.scaleX = 2;
		playWordInSentenceButton.scaleY = 2;
		gameButtonsContainer.add(playWordInSentenceButton);

		// playGraphemeExampleWordButton
		const playGraphemeExampleWordButton = new HUDButton(this, 88, 0, "ui-speak-button");
		playGraphemeExampleWordButton.scaleX = 2;
		playGraphemeExampleWordButton.scaleY = 2;
		gameButtonsContainer.add(playGraphemeExampleWordButton);

		// boardView
		const boardView = this.add.container(800, 512);
		mainContainer.add(boardView);

		// boardDropSite
		const boardDropSite = new SlottedCardDropSite(this, 0, 0);
		boardView.add(boardDropSite);

		// boardDropZoneOverride
		const boardDropZoneOverride = this.add.image(0, 0, "1x1sprite");
		boardDropZoneOverride.scaleX = 1600;
		boardDropZoneOverride.scaleY = 200;
		boardDropZoneOverride.alpha = 0;
		boardDropZoneOverride.alphaTopLeft = 0;
		boardDropZoneOverride.alphaTopRight = 0;
		boardDropZoneOverride.alphaBottomLeft = 0;
		boardDropZoneOverride.alphaBottomRight = 0;
		boardView.add(boardDropZoneOverride);

		// progressBarView
		const progressBarView = this.add.container(800, 16);
		mainContainer.add(progressBarView);

		// progressBar
		const progressBar = new ProgressBar(this, 0, 0);
		progressBarView.add(progressBar);

		// typingView
		const typingView = this.add.container(800, 496);
		mainContainer.add(typingView);

		// playerTypingInput
		const playerTypingInput = new PlayerTypingInput(this, 0, 0);
		typingView.add(playerTypingInput);

		// gameRulesView
		const gameRulesView = this.add.container(1600, 0);
		mainContainer.add(gameRulesView);

		// ruleTooltipContainer
		const ruleTooltipContainer = this.add.container(-16, 120);
		gameRulesView.add(ruleTooltipContainer);

		// rulesHighlight
		const rulesHighlight = this.add.nineslice(-120, 56, "frame-common", undefined, 124, 60, 10, 10, 10, 10);
		rulesHighlight.scaleX = 2;
		rulesHighlight.scaleY = 2;
		rulesHighlight.visible = false;
		rulesHighlight.tint = 3450588;
		ruleTooltipContainer.add(rulesHighlight);

		// rulesBG
		const rulesBG = this.add.nineslice(3, -33, "Hud2", undefined, 80, 34, 10, 10, 10, 10);
		rulesBG.scaleX = 3;
		rulesBG.scaleY = 3;
		rulesBG.setOrigin(1, 0);
		ruleTooltipContainer.add(rulesBG);

		// rule1
		const rule1 = this.add.container(-204, 0);
		ruleTooltipContainer.add(rule1);

		// gameRule1
		const gameRule1 = this.add.bitmapText(24, 0, "Aronui", "Capital Letter");
		gameRule1.setOrigin(0, 0.5);
		gameRule1.tintTopLeft = 5387071;
		gameRule1.tintTopRight = 5387071;
		gameRule1.tintBottomLeft = 5387071;
		gameRule1.tintBottomRight = 5387071;
		gameRule1.text = "Capital Letter";
		gameRule1.fontSize = -24;
		gameRule1.maxWidth = 450;
		rule1.add(gameRule1);

		// rule1Checkbox
		const rule1Checkbox = new CheckboxSmall(this, 0, 0);
		rule1.add(rule1Checkbox);

		// rule2
		const rule2 = this.add.container(-204, 36);
		ruleTooltipContainer.add(rule2);

		// gameRule2
		const gameRule2 = this.add.bitmapText(24, 0, "Aronui", "Fullstop");
		gameRule2.setOrigin(0, 0.5);
		gameRule2.tintTopLeft = 5387071;
		gameRule2.tintTopRight = 5387071;
		gameRule2.tintBottomLeft = 5387071;
		gameRule2.tintBottomRight = 5387071;
		gameRule2.text = "Fullstop";
		gameRule2.fontSize = -24;
		gameRule2.maxWidth = 450;
		rule2.add(gameRule2);

		// rule2Checkbox
		const rule2Checkbox = new CheckboxSmall(this, 0, 0);
		rule2.add(rule2Checkbox);

		// instructionsView
		const instructionsView = new MinigameInstructions(this, 512, 384);
		mainContainer.add(instructionsView);

		// gameFeedbackView
		const gameFeedbackView = this.add.container(800, 512);
		mainContainer.add(gameFeedbackView);

		// speedBonusFeedbackContainer
		const speedBonusFeedbackContainer = new SpeedBonusFeedbackContainer(this, 0, 0);
		speedBonusFeedbackContainer.visible = false;
		gameFeedbackView.add(speedBonusFeedbackContainer);

		// minigameOptionsContainer
		const minigameOptionsContainer = this.add.container(1600, 0);
		minigameOptionsContainer.scaleX = 1.5;
		minigameOptionsContainer.scaleY = 1.5;

		// nineslice_1
		const nineslice_1 = this.add.nineslice(-9, 8, "Hud3", undefined, 54, 24, 12, 12, 12, 12);
		nineslice_1.scaleX = 2;
		nineslice_1.scaleY = 2;
		nineslice_1.setOrigin(1, 0);
		minigameOptionsContainer.add(nineslice_1);

		// quitButton
		const quitButton = new HUDButton(this, -40, 32, "exit1");
		quitButton.scaleX = 2;
		quitButton.scaleY = 2;
		minigameOptionsContainer.add(quitButton);

		// settingsButton
		const settingsButton = new HUDButton(this, -76, 32, "hud-icons", 131);
		settingsButton.scaleX = 2;
		settingsButton.scaleY = 2;
		minigameOptionsContainer.add(settingsButton);

		// swapControlsButton
		const swapControlsButton = new HUDButton(this, -48, 48, "KeyboardClick");
		swapControlsButton.scaleX = 2;
		swapControlsButton.scaleY = 2;
		swapControlsButton.visible = false;
		minigameOptionsContainer.add(swapControlsButton);

		// helpButton
		const helpButton = new HUDButton(this, -48, 48, "help3");
		helpButton.scaleX = 2;
		helpButton.scaleY = 2;
		helpButton.visible = false;
		minigameOptionsContainer.add(helpButton);

		// noTypingNotification
		const noTypingNotification = new NoTypingNotification(this, 0, 60);
		noTypingNotification.visible = false;
		minigameOptionsContainer.add(noTypingNotification);

		// definitionExampleView
		const definitionExampleView = new DefinitionExampleView(this, 0, 0);
		this.add.existing(definitionExampleView);

		// articulatoryGesturesView
		const articulatoryGesturesView = this.add.container(0, 0);
		articulatoryGesturesView.visible = false;

		// uIBlocker_2
		const uIBlocker_2 = new UIBlocker(this, 0, 0);
		articulatoryGesturesView.add(uIBlocker_2);

		// articulatoryGesturesContainer
		const articulatoryGesturesContainer = this.add.container(800, 512);
		articulatoryGesturesView.add(articulatoryGesturesContainer);

		// debugLight
		const debugLight = this.add.image(0, 0, "1x1sprite");
		debugLight.scaleX = 50;
		debugLight.scaleY = 50;
		debugLight.setOrigin(0, 0);
		debugLight.visible = false;

		// minigameScoreScreen
		const minigameScoreScreen = new MinigameScoreScreen(this, 0, 0);
		this.add.existing(minigameScoreScreen);
		minigameScoreScreen.visible = false;

		// minigameMainLoadingContainer
		const minigameMainLoadingContainer = this.add.container(0, 0);
		minigameMainLoadingContainer.visible = false;

		// uIBlocker_3
		const uIBlocker_3 = new UIBlocker(this, 0, 0);
		minigameMainLoadingContainer.add(uIBlocker_3);

		// loadingSpinner
		const loadingSpinner = new LoadingSpinner(this, 0, 0);
		minigameMainLoadingContainer.add(loadingSpinner);

		// repeatInstructionButton (prefab fields)
		repeatInstructionButton.useAlternateTextures = false;

		// autoWinButton (prefab fields)
		autoWinButton.useAlternateTextures = false;

		// playDefinitionButton (prefab fields)
		playDefinitionButton.useAlternateTextures = false;

		// playWordInSentenceButton (prefab fields)
		playWordInSentenceButton.useAlternateTextures = false;

		// playGraphemeExampleWordButton (prefab fields)
		playGraphemeExampleWordButton.useAlternateTextures = false;

		// settingsButton (prefab fields)
		settingsButton.useAlternateTextures = false;

		this.finalBacking = finalBacking;
		this.bG2 = bG2;
		this.bG = bG;
		this.gameframe = gameframe;
		this.board_BR = board_BR;
		this.board_TL = board_TL;
		this.board_TR = board_TR;
		this.board_BL = board_BL;
		this.handBG = handBG;
		this.submitButton = submitButton;
		this.handDropSite = handDropSite;
		this.handDropZoneOverride = handDropZoneOverride;
		this.handDropSite_1 = handDropSite_1;
		this.handView = handView;
		this.repeatInstructionButton = repeatInstructionButton;
		this.autoWinButton = autoWinButton;
		this.playDefinitionButton = playDefinitionButton;
		this.playWordInSentenceButton = playWordInSentenceButton;
		this.playGraphemeExampleWordButton = playGraphemeExampleWordButton;
		this.gameButtonsContainer = gameButtonsContainer;
		this.playerInputView = playerInputView;
		this.boardDropSite = boardDropSite;
		this.boardDropZoneOverride = boardDropZoneOverride;
		this.boardView = boardView;
		this.progressBar = progressBar;
		this.progressBarView = progressBarView;
		this.playerTypingInput = playerTypingInput;
		this.typingView = typingView;
		this.rulesHighlight = rulesHighlight;
		this.rule1Checkbox = rule1Checkbox;
		this.rule2Checkbox = rule2Checkbox;
		this.gameRulesView = gameRulesView;
		this.instructionsView = instructionsView;
		this.gameFeedbackView = gameFeedbackView;
		this.mainContainer = mainContainer;
		this.quitButton = quitButton;
		this.settingsButton = settingsButton;
		this.swapControlsButton = swapControlsButton;
		this.helpButton = helpButton;
		this.noTypingNotification = noTypingNotification;
		this.minigameOptionsContainer = minigameOptionsContainer;
		this.definitionExampleView = definitionExampleView;
		this.articulatoryGesturesContainer = articulatoryGesturesContainer;
		this.articulatoryGesturesView = articulatoryGesturesView;
		this.minigameScoreScreen = minigameScoreScreen;
		this.loadingSpinner = loadingSpinner;
		this.minigameMainLoadingContainer = minigameMainLoadingContainer;

		this.events.emit("scene-awake");
	}

	private finalBacking!: Phaser.GameObjects.Image;
	private bG2!: Phaser.GameObjects.Image;
	private bG!: Phaser.GameObjects.Image;
	private gameframe!: Phaser.GameObjects.Container;
	private board_BR!: Phaser.GameObjects.Image;
	private board_TL!: Phaser.GameObjects.Image;
	private board_TR!: Phaser.GameObjects.Image;
	private board_BL!: Phaser.GameObjects.Image;
	private handBG!: Phaser.GameObjects.NineSlice;
	private submitButton!: DefaultButton;
	private handDropSite!: SequencedCardDropSite;
	public handDropZoneOverride!: Phaser.GameObjects.Image;
	public handDropSite_1!: SequencedCardDropSite;
	private handView!: Phaser.GameObjects.Container;
	private repeatInstructionButton!: HUDButton;
	private autoWinButton!: HUDButton;
	private playDefinitionButton!: HUDButton;
	private playWordInSentenceButton!: HUDButton;
	private playGraphemeExampleWordButton!: HUDButton;
	private gameButtonsContainer!: Phaser.GameObjects.Container;
	private playerInputView!: Phaser.GameObjects.Container;
	private boardDropSite!: SlottedCardDropSite;
	public boardDropZoneOverride!: Phaser.GameObjects.Image;
	private boardView!: Phaser.GameObjects.Container;
	private progressBar!: ProgressBar;
	private progressBarView!: Phaser.GameObjects.Container;
	private playerTypingInput!: PlayerTypingInput;
	private typingView!: Phaser.GameObjects.Container;
	private rulesHighlight!: Phaser.GameObjects.NineSlice;
	private rule1Checkbox!: CheckboxSmall;
	private rule2Checkbox!: CheckboxSmall;
	private gameRulesView!: Phaser.GameObjects.Container;
	private instructionsView!: MinigameInstructions;
	private gameFeedbackView!: Phaser.GameObjects.Container;
	private mainContainer!: Phaser.GameObjects.Container;
	private quitButton!: HUDButton;
	private settingsButton!: HUDButton;
	private swapControlsButton!: HUDButton;
	private helpButton!: HUDButton;
	private noTypingNotification!: NoTypingNotification;
	private minigameOptionsContainer!: Phaser.GameObjects.Container;
	private definitionExampleView!: DefinitionExampleView;
	private articulatoryGesturesContainer!: Phaser.GameObjects.Container;
	private articulatoryGesturesView!: Phaser.GameObjects.Container;
	private minigameScoreScreen!: MinigameScoreScreen;
	public loadingSpinner!: LoadingSpinner;
	public minigameMainLoadingContainer!: Phaser.GameObjects.Container;

	/* START-USER-CODE */

  minigameController?: MinigameController;
  inputHandler?: StudentGameInputHandler | undefined = undefined;
  buttonLayoutGroup?: HorizontalLayoutGroup;
  boardInteractive: boolean = false;
  articulatoryGestureLayoutGroup?: HorizontalLayoutGroup;
  hasFinishedInitialLoad: boolean = false;

  preload() {
    const minigameSFXKey = 'minigame-sfx';
    if (!this.cache.json.get(minigameSFXKey)) {
      this.load.pack(minigameSFXKey, 'game_assets/audio/sfx/minigame-sfx.json');
    }
    this.loadBoardAssets();
  }

  loadBoardAssets() {
    const biomeIdx = getBiomeFromStudentLevel(getStudent().level);
    const currentBiome = `${biomeKeys[biomeIdx]}`;
    const minigameBoardsKey = `minigame-boards-${currentBiome}`;
    if (!this.cache.json.get(minigameBoardsKey)) {
      this.load.pack(
        minigameBoardsKey,
        `game_assets/sprites/phaser_game/minigame/${currentBiome}/${currentBiome}-board.json`,
      );
    }
    this.load.start();
  }

  getScene() {
    return this as Phaser.Scene;
  }

  override getPlayerTypingInputs() {
    return [this.playerTypingInput];
  }

  getBoardDropSite() {
    return this.boardDropSite;
  }

  getHandDropSite() {
    return this.handDropSite;
  }

  getCanSubmit() {
    return this.minigameController?.canSubmitAnswer() || false;
  }

  getShouldDisplayBoardSlots() {
    return this.minigameController?.getShouldDisplayBoardSlots() || false;
  }

  override getDefinitionExampleView() {
    return this.definitionExampleView;
  }

  override getDefinitionIconsToLoad() {
    const game = getGeneralGameState().currentGame as keyof GameData;
    const gameState = getGeneralGameState().gameData[game];
    if (!gameState) return [];
    if (game === 'mysticMatch') {
      return gameState
        .map((w) =>
          'example' in w ? getWordById(w.example)?.spelling : undefined,
        )
        .filter((e) => e !== undefined);
    }
    return gameState.map((w) => w.spelling);
  }

  override getExampleIconsToLoad() {
    const game = getGeneralGameState().currentGame as keyof GameData;
    if (game === 'mysticMatch') return [];
    const gameState = getGeneralGameState().gameData[game];
    if (!gameState) return [];
    return gameState
      .filter((w) => 'example' in w)
      .map((w) => _.kebabCase(w.example));
  }

  create() {
    this.editorCreate();

    this.initialize();
  }

  initialize() {
    const currentGame = getGeneralGameState().currentGame;
    this.instructionsView.controller.setGame(currentGame);
    this.submitButton.controller.makeInteractive(false);
    this.minigameMainLoadingContainer.setVisible(false);

    // Layout group.
    this.articulatoryGestureLayoutGroup = new HorizontalLayoutGroup({
      parent: this.articulatoryGesturesContainer,
      origin: new Phaser.Math.Vector2(0.5, 0.5),
      elementDist: 16,
      align: 'center',
    });

    // Button callbacks.
    this.instructionsView.controller.onInstructionsViewSubmitted = () => {
      this.startGame(currentGame);
    };

    this.instructionsView.controller.onInstructionsViewCancelled = () => {
      this.quitMinigame();
    };

    this.playDefinitionButton.setSubmissionCallback(() => {
      this.minigameController?.playCurrentWordDefinition();
    });

    this.playWordInSentenceButton.setSubmissionCallback(() => {
      this.minigameController?.playWordInSentence();
    });

    this.playGraphemeExampleWordButton.setSubmissionCallback(() => {
      this.minigameController?.playGraphemeExampleWord();
    });

    this.repeatInstructionButton.setSubmissionCallback(() => {
      this.playInstructions();
    });

    this.submitButton.controller.setSubmissionCallback(() => {
      this.checkAnswer();
    });

    this.quitButton.setSubmissionCallback(() => {
      this.quitMinigame();
    });

    this.settingsButton.setSubmissionCallback(() => {
      SettingsPanelController.openWithConfiguration({});
    });

    this.autoWinButton.setSubmissionCallback(() => {
      this.autoWinMinigame();
    });

    // Board style.
    const biome = getBiomeFromStudentLevel(getStudent().level);
    this.setBiomeView(biome);

    // Responsiveness.
    this.initializeResponsiveness();

    this.minigameScoreScreen.controller.initialize();

    this.startInitialLoad();

    this.definitionExampleView.setVisible(false);
    this.rule1Checkbox.brain.setIsInteractive(false);
    this.rule2Checkbox.brain.setIsInteractive(false);

    // Event Listeners.
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.MINIGAME_CORRECT_ANSWER,
      this.handleCorrectAnswer,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.MINIGAME_INCORRECT_ANSWER,
      this.handleIncorrectAnswer,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.INPUT_METHOD_CHANGED,
      this.handleInputMethodChanged,
      this,
    );
    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.STUDENT_GAME_KEYBOARD_INPUT,
      this.handleStudentGameKeyboardInput,
      this,
    );

    EventsManager.registerEventOn(
      this,
      GAME_EVENTS.SETTINGS_PANEL_CLOSED,
      this.handleSettingsPanelClosed,
      this,
    );
  }

  async startInitialLoad() {
    const game = getGeneralGameState().currentGame;
    this.setInstructionsView(game);
    await GameInstance.instance.startActivity(game);
    await this.preloadHelperIcons();
    this.hasFinishedInitialLoad = true;
  }

  setInstructionsView(_game: string) {
    this.instructionsView.setVisible(true);
    this.minigameOptionsContainer.setVisible(false);
    this.progressBarView.setVisible(false);
    this.boardView.setVisible(false);
    this.handView.setVisible(false);
    this.playerInputView.setVisible(false);
    this.minigameScoreScreen.setVisible(false);
    this.typingView.setVisible(false);
    this.noTypingNotification.setVisible(false);
    this.gameRulesView.setVisible(false);
  }

  async startGame(game: string) {
    // Generic setup.
    this.instructionsView.setVisible(false);
    this.minigameMainLoadingContainer.setVisible(true);
    await waitUntil(this.time, () => getSession().currentActivity !== null);
    await waitUntil(this.time, () => this.hasFinishedInitialLoad);
    this.minigameMainLoadingContainer.setVisible(false);
    this.autoWinButton.setVisible(isPhaserDevMode(getStudent()));
    this.playDefinitionButton.setVisible(shouldShowWordHelperButtons(game));
    this.playWordInSentenceButton.setVisible(shouldShowWordHelperButtons(game));
    this.playGraphemeExampleWordButton.setVisible(
      shouldShowGraphemeExampleWordButton(game),
    );
    this.buttonLayoutGroup?.updateLayout();
    this.minigameOptionsContainer.setVisible(true);
    this.noTypingNotification.setVisible(!this.shouldAllowKeyboardInput(game));

    const student = getStudent();
    if (!student) {
      console.error('No student found');
      return;
    }
    // For now, we only show the game rules view for the sentence builder game.
    if (
      game === 'sentenceBuilder' &&
      this.getControlScheme(student.settings.controllerType) === 'keyboard'
    ) {
      this.gameRulesView.setVisible(true);
    }

    this.progressBarView.setVisible(true);
    this.setupBoardForControlScheme(
      this.getControlScheme(getStudent().settings.controllerType),
    );

    switch (game) {
      case 'mysticMatch':
        this.minigameController = new MysticMatchController(this);
        break;
      case 'phonemeForge':
        this.minigameController = new PhonemeForgeController(this);
        break;
      case 'wordWeaver':
        this.minigameController = new WordWeaverController(this);
        break;
      case 'trickyWords':
        this.minigameController = new HeartWordsController(this);
        break;
      case 'sentenceBuilder':
        this.minigameController = new SentenceBuilderController(this);
        break;
      case 'wordRifts':
        this.minigameController = new WordRiftsController(this);
        break;
      case 'wordSums':
        this.minigameController = new WordSumsController(this);
        break;
      case 'morphMaster':
        this.minigameController = new MorphMasterController(this);
        break;
    }

    if (!this.minigameController) {
      throw new Error('Minigame controller not found');
    }

    this.inputHandler = new StudentGameInputHandler(this);
    this.newRound();
  }

  async newRound() {
    if (!this.minigameController) {
      throw new Error('Minigame controller not found');
    }

    // Clear currrent cards.
    this.clearCards();

    // Clear input text.
    this.clearTypedInput();

    this.minigameController.startRound();
    // Force the progress bar to update.
    this.progressBar.handleResize();

    // This will also update the board's layout.
    this.boardDropSite.updateMaxCards(
      this.minigameController.getMaxBoardCards(),
    );

    // This will initialize the cards, populate card list etc.
    this.minigameController.createCardList(this);

    //this.boardDropSite.layoutGroup?.updateLayout();
    GameInstance.instance.resetCurrentAttemptStartedAt();
    this.setBoardInteractive(true);
    await waitForSeconds(this.time, 0.5);
    await this.minigameController.playAudioForRound();

    this.submitButton.controller.makeInteractive(
      this.minigameController?.canSubmitAnswer() || false,
    );

    GameInstance.instance.resetCurrentAttemptStartedAt();

    // Start hint timeout.
    EventBus.emit(GAME_EVENTS.STUDENT_GAME_ROUND_STARTED);
  }

  // Sets up event listeners etc. for a newly created minigame card.
  initializeNewMinigameCard(
    newCard: StudentGameCard,
    data: MinigameCardData,
    dropSite: 'hand' | 'board',
    index?: number,
  ) {
    newCard.minigame = this;
    newCard.cardData = data;
    if (data.word) {
      newCard.setCardText(data.word.spelling);
    } else if (data.grapheme) {
      newCard.setCardText(data.grapheme.spelling);
    } else if (data.spelling) {
      newCard.setCardText(data.spelling);
    } else if (data.morpheme) {
      newCard.setCardText(data.morpheme.spelling);
    }

    this.inputHandler?.initializeCardInput(newCard);

    const parentDropSite =
      dropSite === 'board' ? this.boardDropSite : this.handDropSite;
    this.minigameController?.initializeNewMinigameCard(
      newCard,
      parentDropSite,
      index,
    );

    const initialX = parentDropSite.x;
    const initialY = parentDropSite === this.handDropSite ? -4 : 0;
    newCard.setPosition(initialX, initialY);
    newCard.origin = {
      dropSite: parentDropSite,
      index: parentDropSite.layoutGroup?.cards.indexOf(newCard) ?? 0,
    };
  }

  updateCandidatePlacement(
    dropSite: CardDropSite,
    card: StudentGameCard,
    targetIndex: number,
  ) {
    // Remove existing candidate placements.
    if (dropSite !== this.handDropSite) {
      this.handDropSite.removeCandidatePlacement();
    }
    if (dropSite !== this.boardDropSite) {
      this.boardDropSite.removeCandidatePlacement();
    }
    dropSite.displayCandidatePlacement(targetIndex, card);
  }

  async showScoreScreen() {
    await this.minigameController?.finishActivity();
    this.minigameOptionsContainer.setVisible(false);
    this.noTypingNotification.setVisible(false);
    this.typingView.setVisible(false);
    this.gameRulesView.setVisible(false);

    this.minigameScoreScreen.controller.showScoreScreen(this);
    const { currentActivity } = GameInstance.instance.session;
    if (!currentActivity) {
      console.error('No current activity found');
      return;
    }

    this.playerInputView.setVisible(false);
    this.handView.setVisible(false);
    this.boardView.setVisible(false);
    this.progressBarView.setVisible(false);
  }

  async playInstructions() {
    stopAudio();
    await this.minigameController?.playInstructions();
  }

  // Pass in the student's preferred controller type.
  // Return the controller type that should be used for the current game.
  override getControlScheme(preferredControl: ControllerType) {
    const game = getGeneralGameState().currentGame;
    if (this.shouldAllowKeyboardInput(game)) {
      return preferredControl;
    } else {
      // Keyboard defaults to click to place for these games.
      return preferredControl === ControllerType.KEYBOARD
        ? ControllerType.CLICK
        : preferredControl;
    }
  }

  shouldAllowKeyboardInput(game: string) {
    return ['wordWeaver', 'wordSums', 'sentenceBuilder'].includes(game);
  }

  checkAnswer() {
    EventBus.emit(GAME_EVENTS.MINIGAME_CHECK_ANSWER);
    stopAudio();
    this.submitButton.controller.makeInteractive(false);
    this.minigameController?.checkMinigameAnswer();
  }

  setSubmissionPossible(canSubmit: boolean) {
    this.submitButton.controller.makeInteractive(canSubmit);
  }

  setBoardInteractive(interactive: boolean) {
    this.boardInteractive = interactive;
    this.getAllCards().forEach((card) => {
      card.makeInteractive(interactive, false);
    });
    this.submitButton.controller.makeInteractive(interactive);
    this.helpButton.setInteractionEnabled(interactive);
    this.swapControlsButton.setInteractionEnabled(interactive);
    this.repeatInstructionButton.setInteractionEnabled(interactive);
    this.quitButton.setInteractionEnabled(interactive);
    this.settingsButton.setInteractionEnabled(interactive);
    this.playWordInSentenceButton.setInteractionEnabled(interactive);
    this.playDefinitionButton.setInteractionEnabled(interactive);
    this.playerTypingInput.setIsInteractive(interactive);
  }

  quitMinigame(completedSuccessfully: boolean = false) {
    // Return to the level map.
    getSceneManager()?.launchScene('LevelMap', LevelMap, {
      type: 'gameplay',
      stopActiveScene: true,
    });
    if (completedSuccessfully) {
      EventBus.emit(GAME_EVENTS.MINIGAME_COMPLETED_SUCCESSFULLY);
      this.minigameController?.playerCompleteActivity('mysticMatch');
      this.minigameController?.completedNode();
    }
    this.minigameController?.handleQuit(completedSuccessfully);
    EventBus.emit(GAME_EVENTS.MINIGAME_ENDED);
  }

  autoWinMinigame() {
    this.minigameController?.autoWinMinigame();
    this.showScoreScreen();
  }

  async handleCorrectAnswer(attemptData: any) {
    if (!this.minigameController) {
      console.error('no minigame controller');
      return;
    }

    // Update progress.
    const progress = getGeneralGameState().progress / 100;
    this.progressBar.brain.setProgressAnimated(progress);

    // Speed bonus.
    if (didAttemptEarnSpeedBonus(attemptData)) {
      this.handleSpeedBonusEarned();
    }

    if (progress >= 1) {
      // Show score screen.
      await waitForSeconds(this.time, 1.5);
      this.showScoreScreen();
    } else {
      // Deal a new hand.
      await waitForSeconds(this.time, 1);
      this.newRound();
    }
  }

  handleIncorrectAnswer() {
    // Normally handled by newRound() which is not always called after incorrect
    // answer (except in Mystic Match & Heart Words, but it's ok to call twice).
    GameInstance.instance.resetCurrentAttemptStartedAt();
  }

  setBiomeView(biome: number) {
    const biomeKey = biomeKeys[biome];

    const BLTexKey = `${biomeKey}-board-BL`;
    const TLTexKey = `${biomeKey}-board-TL`;
    const TRTexKey = `${biomeKey}-board-TR`;
    const BRTexKey = `${biomeKey}-board-BR`;
    const BG1TexKey = `${biomeKey}-board-BG1`;
    const BG2TexKey = `${biomeKey}-board-BG2`;

    this.board_BL.setVisible(this.textures.exists(BLTexKey));
    this.board_BL.setTexture(`${biomeKey}-board-BL`);
    this.board_TL.setVisible(this.textures.exists(TLTexKey));
    this.board_TL.setTexture(`${biomeKey}-board-TL`);
    this.board_TR.setVisible(this.textures.exists(TRTexKey));
    this.board_TR.setTexture(`${biomeKey}-board-TR`);
    this.board_BR.setVisible(this.textures.exists(BRTexKey));
    this.board_BR.setTexture(`${biomeKey}-board-BR`);
    this.bG.setVisible(this.textures.exists(BG1TexKey));
    this.bG.setTexture(`${biomeKey}-board-BG1`);
    this.bG2.setVisible(this.textures.exists(BG2TexKey));
    this.bG2.setTexture(`${biomeKey}-board-BG2`);
  }

  clearAnticipation(exceptCard?: StudentGameCard) {
    this.getAllCards().forEach((card) => {
      if (card === exceptCard) return;
      card.setAnticipation(false);
    });
  }

  showHelperArticulatoryGestures(gestures: string[], audioDelay: number) {
    //this.showHelperVisual(gesture, audioDelay);
    this.articulatoryGestureLayoutGroup?.clear();
    for (const gesture of gestures) {
      const newGesture = new ArticulatoryGesture(
        this,
        0,
        0,
        getArticulatoryGestureTexture(gesture),
      );
      this.articulatoryGestureLayoutGroup?.addElement(newGesture);
    }
    this.articulatoryGesturesView.setVisible(true);
    this.articulatoryGesturesView.setAlpha(0);
    this.tweens.killTweensOf(this.articulatoryGesturesView);

    this.tweens.add({
      targets: this.articulatoryGesturesView,
      alpha: 1,
      duration: audioDelay,
      ease: 'Power2',
      yoyo: true,
      onComplete: () => {
        this.articulatoryGesturesView.setVisible(true);
      },
    });
  }

  setupBoardForControlScheme(scheme: ControllerType) {
    this.playerInputView.setVisible(true);
    if (scheme === ControllerType.KEYBOARD) {
      this.handView.setVisible(false);
      this.boardView.setVisible(false);
      this.typingView.setVisible(true);
      // TODO: Remove the special casing once we have a general way to show the game rules view.
      this.gameRulesView.setVisible(
        getGeneralGameState().currentGame === 'sentenceBuilder',
      );
    } else {
      this.handView.setVisible(true);
      this.boardView.setVisible(true);
      this.typingView.setVisible(false);
      this.gameRulesView.setVisible(false);
    }
    this.minigameController?.updateRules(scheme);
  }

  async riffleItems() {
    const student = getStudent();
    if (!student) {
      console.error('No student found');
      return;
    }
    if (this.getControlScheme(student.settings.controllerType) === 'keyboard') {
      await this.getPlayerTypingInputs().forEach((input: PlayerTypingInput) =>
        input.riffleLetters(),
      );
    } else {
      await this.getBoardDropSite().riffleCards();
    }
  }

  setRulesVisible(visible: boolean) {
    this.gameRulesView.setVisible(visible);
  }

  setRulesStatus(status: boolean[]) {
    const checkboxes = [this.rule1Checkbox, this.rule2Checkbox];
    checkboxes.forEach((checkbox, index) => {
      checkbox.brain.setSelected(status[index], true);
    });
  }

  highlightRules() {
    this.rulesHighlight.setVisible(true);
    this.rulesHighlight.setAlpha(0);
    this.tweens.add({
      targets: this.rulesHighlight,
      alpha: 1,
      duration: 1000,
      ease: 'Power2',
      yoyo: true,
      onComplete: () => {
        this.rulesHighlight.setVisible(false);
      },
    });
    DoReinforcementFeedback(this, this.gameRulesView);
    PhaserAudioManager.playSFX(this, 'incorrect-sfx');
  }

  loadMinigameAssets() {
    if (!this.minigameController) return;

    const definitionIconsToLoad = this.getDefinitionIconsToLoad();
    definitionIconsToLoad.forEach((fileKey: string) => {
      this.load.image(fileKey, `game_assets/definition_images/${fileKey}.png`);
    });

    const exampleIconsToLoad = this.getExampleIconsToLoad();
    exampleIconsToLoad.forEach((fileKey: string) => {
      this.load.image(fileKey, `game_assets/example_images/${fileKey}.png`);
    });
    this.load.start();
  }

  async initializeResponsiveness() {
    await waitForMilliseconds(this.time, 1);
    this.buttonLayoutGroup = new HorizontalLayoutGroup({
      parent: this.gameButtonsContainer,
      origin: new Phaser.Math.Vector2(0, 0),
      elementDist: 8,
      align: 'center',
      realignmentType: 'instant',
    });

    const anchor = getAnchorPlugin(this);
    anchor.add(this.loadingSpinner, {
      centerX: '50%',
      centerY: '50%',
    });

    anchor.add(this.mainContainer, {
      centerX: '0%',
      centerY: '0%',
    });

    anchor.add(this.board_BL, {
      left: '0%',
      bottom: '100%',
    });

    anchor.add(this.board_TL, {
      left: '0%',
      top: '0%',
    });

    anchor.add(this.board_TR, {
      top: '0%',
      right: '100%',
    });

    anchor.add(this.board_BR, {
      right: '100%',
      bottom: '100%',
    });

    anchor.add(this.boardView, {
      centerX: '50%',
      centerY: '50%-120',
    });
    anchor.add(this.progressBarView, {
      top: '0%+16',
      centerX: `25%`,
    });
    anchor.add(this.playerInputView, {
      bottom: '100%-16',
      centerX: '50%',
      width: '80%',
    });

    anchor.add(this.finalBacking, {
      width: '100%',
      height: '100%',
    });

    anchor.add(this.minigameOptionsContainer, {
      right: '100%',
      top: '0%',
    });
    anchor.add(this.articulatoryGesturesContainer, {
      centerX: '50%',
      centerY: '50%',
    });
    anchor.add(this.typingView, {
      centerX: '50%',
      centerY: '50%',
    });
    anchor.add(this.gameRulesView, {
      right: '100%',
    });
    anchor.add(this.gameFeedbackView, {
      centerX: '50%',
      centerY: '50%',
    });

    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      this.playerInputView,
      DEFAULT_SCALE_DOWN_CONFIG,
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      this.minigameOptionsContainer,
      { ...DEFAULT_SCALE_DOWN_CONFIG, scaleModifier: 1.5 },
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      this.gameRulesView,
      DEFAULT_SCALE_DOWN_CONFIG,
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      this.typingView,
      DEFAULT_SCALE_DOWN_CONFIG,
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(
      this.boardView,
      DEFAULT_SCALE_DOWN_CONFIG,
    );
    ResponsivenessManager.MakeElementScaleDownWithGameSize(this.gameframe, {
      ...DEFAULT_SCALE_DOWN_CONFIG,
      scaleModifier: 4,
    });
    ResponsivenessManager.MakeElementScaleDownWithGameSize(this.board_BL, {
      ...DEFAULT_SCALE_DOWN_CONFIG,
      scaleModifier: 2,
    });
    ResponsivenessManager.MakeElementScaleDownWithGameSize(this.board_BR, {
      ...DEFAULT_SCALE_DOWN_CONFIG,
      scaleModifier: 2,
    });
    ResponsivenessManager.MakeElementScaleDownWithGameSize(this.board_TL, {
      ...DEFAULT_SCALE_DOWN_CONFIG,
      scaleModifier: 2,
    });
    ResponsivenessManager.MakeElementScaleDownWithGameSize(this.board_TR, {
      ...DEFAULT_SCALE_DOWN_CONFIG,
      scaleModifier: 2,
    });

    ResponsivenessManager.InitializeResizeHandler(
      this,
      () => this.handleResize(),
      this,
    );
  }

  handleResize() {
    if (!this.scene || !this.scene.isActive()) return;
    const anchor = getAnchorPlugin(this);
    this.buttonLayoutGroup?.updateLayout();

    switch (ResponsivenessManager.GetCurrentGlobalViewForGame(this)) {
      case 'desktop':
      default:
        anchor.add(this.playerInputView, {
          bottom: '100%-16',
          centerX: '50%',
          width: '80%',
        });
        anchor.add(this.boardView, {
          centerX: '50%',
          centerY: '50%-120',
        });

        break;
      case 'mobile':
        anchor.add(this.playerInputView, {
          bottom: '100%-5',
          centerX: '50%',
          width: '80%',
        });
        const inputHeight = this.playerInputView.getBounds().height;
        const optionsHeight = this.minigameOptionsContainer.getBounds().height;
        const topMargin = 8;
        const boardViewOffset = Math.abs(
          Math.floor(topMargin + optionsHeight - inputHeight),
        );
        anchor.add(this.boardView, {
          centerX: '50%',
          centerY: `50%-${boardViewOffset}`,
        });
        break;
    }
  }

  handleInputMethodChanged(newType: ControllerType) {
    if (this.getControlScheme(newType) !== newType) return; // We tried to use an input method that's not allowed for this game.
    this.setupBoardForControlScheme(newType);
  }

  handleStudentGameKeyboardInput(_input: string) {
    if (
      this.getControlScheme(getStudent().settings.controllerType) !==
      ControllerType.KEYBOARD
    )
      return;
    this.minigameController?.updateRules(ControllerType.KEYBOARD);
    this.getDefinitionExampleView().setVisible(false);
    this.tweens.killTweensOf(this.getDefinitionExampleView());
  }

  handleSettingsPanelClosed() {
    // TODO make a UI manager.
    if (getSceneManager()?.activeSceneKey === 'Minigame') {
      getSceneManager()?.resumeScene('Minigame');
    }
  }

  handleSpeedBonusEarned() {
    // Uncomment this if we want to re-enable the speed bonus feedback.
    //this.speedBonusFeedbackContainer.showSpeedBonusFeedback(2);
  }
  /* END-USER-CODE */
}

/* END OF COMPILED CODE */

// You can write more code here
